/* ──────────────────────────────────────────────────────────────
   editor/catalogManager.tsx · reusable "manage a list" section
   Chips with edit (rename) + delete (confirm) + a "+" create modal.
   Used for Collections and Locations inside the item editor.
   ────────────────────────────────────────────────────────────── */
import React, { useState, useEffect, useCallback } from 'react';
import { View, Text, TouchableOpacity, Platform, ViewStyle } from 'react-native';
import { useTheme } from '../../context/themeContext';
import { Icon, Button, Modal, ConfirmDialog, useToast } from '../../components/ui';
import CommonInput from '../../components/commonInput';
import { handleApiError } from '../../utils/errorHandler';

export interface NamedRow { id: string; name: string }

interface Props {
  title: string;
  loadAll: () => Promise<NamedRow[]>;
  /** Omit to hide the create affordance — e.g. locations, which mirror a
   *  branch platform owns and so cannot be invented here. */
  onCreate?: (name: string) => Promise<any>;
  onRename: (id: string, name: string) => Promise<any>;
  onDelete: (id: string) => Promise<void>;
  /** Optional press action on a chip (e.g. "add this item to the collection"). */
  onChip?: (row: NamedRow) => void | Promise<void>;
  chipHint?: string;          // small helper under the chips
  createLabel?: string;       // modal title for create
  emptyText?: string;
  reloadKey?: any;            // bump to force a reload
}

export default function CatalogManager({
  title, loadAll, onCreate, onRename, onDelete, onChip, chipHint, createLabel, emptyText, reloadKey,
}: Props) {
  const { theme, isDark } = useTheme();
  const { colors, fontSize, fontWeight, radii, shadows } = theme;
  const toast = useToast();

  const [rows, setRows] = useState<NamedRow[]>([]);
  const [modal, setModal] = useState<{ mode: 'create' | 'edit'; id?: string } | null>(null);
  const [name, setName] = useState('');
  const [saving, setSaving] = useState(false);
  const [del, setDel] = useState<NamedRow | null>(null);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async () => {
    try { setRows(await loadAll()); } catch { /* non-fatal */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reloadKey]);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    const n = name.trim();
    if (!n) return;
    setSaving(true);
    try {
      if (modal?.mode === 'edit' && modal.id) {
        await onRename(modal.id, n);
        setRows(prev => prev.map(r => (r.id === modal.id ? { ...r, name: n } : r)));
      } else {
        if (!onCreate) return;
        const created = await onCreate(n);
        const id = created?.id ?? created?.data?.id;
        setRows(prev => [...prev, { id: id ?? String(Math.random()), name: n }]);
      }
      toast.success(modal?.mode === 'edit' ? 'Renamed' : 'Created');
      setModal(null);
    } catch (err) {
      handleApiError(err, 'Could not save');
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!del) return;
    setDeleting(true);
    try {
      await onDelete(del.id);
      setRows(prev => prev.filter(r => r.id !== del.id));
      toast.success('Deleted');
      setDel(null);
    } catch (err) {
      handleApiError(err, 'Could not delete');
    } finally {
      setDeleting(false);
    }
  };

  const sectionCard: ViewStyle = {
    backgroundColor: colors.card, borderWidth: 1, borderColor: colors.line, borderRadius: radii.r3,
    marginTop: 16, ...(shadows.sm as any),
  };

  return (
    <View style={sectionCard}>
      <View style={{ paddingVertical: 13, paddingHorizontal: 16, borderBottomWidth: 1, borderBottomColor: colors.line, flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
        <Text style={{ fontSize: fontSize.md, fontWeight: fontWeight.semibold, color: colors.ink, fontFamily: theme.font.sans }}>{title}</Text>
        {onCreate ? (
          <TouchableOpacity
            onPress={() => { setName(''); setModal({ mode: 'create' }); }}
            style={{ width: 28, height: 28, borderRadius: 8, backgroundColor: colors.card, borderWidth: 1, borderColor: colors.lineStrong, alignItems: 'center', justifyContent: 'center', ...Platform.select({ web: { cursor: 'pointer' } as any, default: {} }) }}
          >
            <Icon name="plus" size={14} color={colors.ink2} />
          </TouchableOpacity>
        ) : null}
      </View>
      <View style={{ padding: 16, gap: 8 }}>
        {rows.length === 0 ? (
          <Text style={{ fontSize: fontSize.sm, color: colors.ink3, fontFamily: theme.font.sans }}>{emptyText || `No ${title.toLowerCase()} yet.`}</Text>
        ) : (
          <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8 }}>
            {rows.map(r => (
              <View key={r.id} style={{ flexDirection: 'row', alignItems: 'center', gap: 6, paddingLeft: 12, paddingRight: 6, paddingVertical: 6, borderRadius: 999, borderWidth: 1, borderColor: colors.line, backgroundColor: colors.card }}>
                <TouchableOpacity disabled={!onChip} onPress={() => onChip?.(r)}>
                  <Text style={{ fontSize: fontSize.sm, color: colors.ink2, fontFamily: theme.font.sans }}>{r.name}</Text>
                </TouchableOpacity>
                <TouchableOpacity onPress={() => { setName(r.name); setModal({ mode: 'edit', id: r.id }); }} hitSlop={{ top: 6, bottom: 6, left: 4, right: 4 }} style={{ padding: 2 }}>
                  <Icon name="edit" size={12} color={colors.ink3} />
                </TouchableOpacity>
                <TouchableOpacity onPress={() => setDel(r)} hitSlop={{ top: 6, bottom: 6, left: 4, right: 6 }} style={{ padding: 2 }}>
                  <Icon name="x" size={12} color={colors.ink3} />
                </TouchableOpacity>
              </View>
            ))}
          </View>
        )}
        {!!chipHint && <Text style={{ fontSize: fontSize.xs, color: colors.ink3, fontFamily: theme.font.sans }}>{chipHint}</Text>}
      </View>

      <Modal
        open={!!modal}
        onClose={() => setModal(null)}
        size="sm"
        title={modal?.mode === 'edit' ? `Rename ${title.slice(0, -1).toLowerCase()}` : (createLabel || `New ${title.slice(0, -1).toLowerCase()}`)}
        footer={
          <View style={{ flexDirection: 'row', gap: 8, justifyContent: 'flex-end' }}>
            <Button onPress={() => setModal(null)}>Cancel</Button>
            <Button kind="primary" hoverLift icon="check" disabled={saving || !name.trim()} onPress={save}>
              {saving ? 'Saving…' : 'Save'}
            </Button>
          </View>
        }
      >
        <View style={{ gap: 8 }}>
          <Text style={{ fontSize: fontSize.sm, fontWeight: fontWeight.medium, color: colors.ink2, fontFamily: theme.font.sans }}>Name</Text>
          <CommonInput value={name} onChangeText={setName} placeholder="Name" isDark={isDark} inputProps={{ autoFocus: true, onSubmitEditing: save }} />
        </View>
      </Modal>

      <ConfirmDialog
        visible={!!del}
        title={`Delete ${title.slice(0, -1).toLowerCase()}?`}
        message={del ? `"${del.name}" will be permanently deleted. This can't be undone.` : ''}
        confirmText={deleting ? 'Deleting…' : 'Delete'}
        cancelText="Cancel"
        onConfirm={confirmDelete}
        onCancel={() => setDel(null)}
        onClose={() => setDel(null)}
      />
    </View>
  );
}
